/**
 * Platform chrome — Save / Undo / Redo / Preview / View-mode toggle.
 *
 * Phase 13 P1 (June 2026)
 *
 * Five tiny tools, all in TOP_TOOLBAR with system-chrome weights
 * (0–49 bucket). Each is registered separately so a tenant or
 * domain can disable any one (e.g. read-only review surfaces
 * hide Save).
 *
 * Save semantics
 * --------------
 *
 * The actual persistence call (`PUT /scenario-os/scenes/<id>`)
 * lives outside this tool; the tool emits a `designer.save_request`
 * event on the eventBus and any page-level listener performs the
 * round-trip. This keeps the framework decoupled from API shape:
 * a page that knows it's authoring against the live API listens;
 * an offline preview surface ignores the event.
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import { always, requiresCapability, any, requiresSceneType } from '../predicates';

// ── Back to Library ──────────────────────────────────────────────────

function BackButton() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleBack = () => {
    const domain = searchParams.get('domain');
    if (domain) {
      navigate(`/coach/scene-library/${domain}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <button
      type="button"
      className="designer-tool__button"
      data-tool-id="platform.back"
      aria-label="Back to scene library"
      title="Back to library"
      onClick={handleBack}
    >
      ← Back
    </button>
  );
}

export const backButton = new DesignerTool({
  id:         'platform.back',
  label:      'Back',
  slot:       'LEFT_RAIL',
  category:   'navigation',
  applies_to: always,
  Component:  BackButton,
  weight:     1,
  helpText:   'Return to the scene library.',
});

// ── Save ────────────────────────────────────────────────────────────

function SaveButton() {
  const { scene, eventBus } = useDesigner();
  return (
    <button
      type="button"
      className="designer-tool__button designer-tool__button--primary"
      data-tool-id="platform.save"
      disabled={!scene}
      aria-label="Save scene"
      title="Save (⌘S)"
      onClick={() => eventBus.emit('designer.save_request', { scene })}
    >
      Save
    </button>
  );
}

export const saveButton = new DesignerTool({
  id:         'platform.save',
  label:      'Save',
  slot:       'LEFT_RAIL',
  category:   'navigation',
  applies_to: always,
  Component:  SaveButton,
  weight:     5,
  keybinding: 'mod+s',
  helpText:   'Persist scene edits to the server.',
});

// ── Undo / Redo ─────────────────────────────────────────────────────

function UndoButton() {
  const { undo, canUndo } = useDesigner();
  return (
    <button
      type="button"
      className="designer-tool__button"
      data-tool-id="platform.undo"
      disabled={!canUndo}
      aria-label="Undo last edit"
      title="Undo (⌘Z)"
      onClick={undo}
    >
      ↶
    </button>
  );
}

function RedoButton() {
  const { redo, canRedo } = useDesigner();
  return (
    <button
      type="button"
      className="designer-tool__button"
      data-tool-id="platform.redo"
      disabled={!canRedo}
      aria-label="Redo edit"
      title="Redo (⌘⇧Z)"
      onClick={redo}
    >
      ↷
    </button>
  );
}

export const undoButton = new DesignerTool({
  id:         'platform.undo',
  label:      'Undo',
  slot:       'LEFT_RAIL',
  category:   'navigation',
  applies_to: always,
  Component:  UndoButton,
  weight:     6,
  keybinding: 'mod+z',
  helpText:   'Reverse the last scene edit.',
});

export const redoButton = new DesignerTool({
  id:         'platform.redo',
  label:      'Redo',
  slot:       'LEFT_RAIL',
  category:   'navigation',
  applies_to: always,
  Component:  RedoButton,
  weight:     7,
  keybinding: 'mod+shift+z',
  helpText:   'Replay the last undone edit.',
});

// ── Preview ─────────────────────────────────────────────────────────

function PreviewButton() {
  const { scene, eventBus } = useDesigner();
  return (
    <button
      type="button"
      className="designer-tool__button"
      data-tool-id="platform.preview"
      disabled={!scene}
      aria-label="Preview scene as learner"
      title="Preview"
      onClick={() => eventBus.emit('designer.preview_request', { scene })}
    >
      ▶ Preview
    </button>
  );
}

export const previewButton = new DesignerTool({
  id:         'platform.preview',
  label:      'Preview',
  slot:       'LEFT_RAIL',
  category:   'navigation',
  applies_to: any(
    requiresCapability('has_actors'),
    requiresCapability('has_timeline'),
    requiresCapability('multiple_choice'),
    requiresCapability('playback_control'),
    requiresSceneType('content_panel'),
  ),
  Component:  PreviewButton,
  weight:     8,
  helpText:   'Open the learner-side preview for this scene.',
});

// ── View-mode toggle (2D / 3D) ──────────────────────────────────────
//
// Only meaningful for scenes that have an actor surface (football,
// basketball, etc.). Bound on `has_actors` capability rather than
// scene_type so the catalog's capability declaration alone gates it.

function ViewModeToggle() {
  const { scene, eventBus, mutateScene } = useDesigner();
  if (!scene) return null;

  const mode = scene.editor_view_mode || '2d';
  const next = mode === '2d' ? '3d' : '2d';

  return (
    <button
      type="button"
      className="designer-tool__button"
      data-tool-id="platform.view_mode_toggle"
      aria-label={`Switch to ${next.toUpperCase()} view`}
      title={`Switch to ${next.toUpperCase()}`}
      onClick={() => {
        mutateScene({ editor_view_mode: next });
        eventBus.emit('designer.view_mode_changed', { mode: next });
      }}
    >
      {mode === '2d' ? '⤴ 3D' : '⤵ 2D'}
    </button>
  );
}

export const viewModeToggle = new DesignerTool({
  id:         'platform.view_mode_toggle',
  label:      'View mode',
  slot:       'LEFT_RAIL',
  category:   'navigation',
  applies_to: requiresCapability('has_actors'),
  Component:  ViewModeToggle,
  weight:     9,
  keybinding: 'mod+m',
  helpText:   'Toggle between 2D top-down and 3D field views.',
});

// ── Bulk export ─────────────────────────────────────────────────────

export const navigationTools = [
  backButton, saveButton, undoButton, redoButton, previewButton, viewModeToggle,
];

export default navigationTools;
